import { h } from '@omni-torn/ui';
import {
  applyCheck,
  checkToObservations,
  cleanEventOptions,
  cleanFilters,
  DEFAULT_CUSTOM,
  DEFAULT_EVENT,
  EVENT_BLURBS,
  ffsFiltersFor,
  finderQuery,
  hittable,
  INACTIVE_DAYS,
  keyMeets,
  ownEliminationTeam,
  parseTargetIds,
  PLAYER_DB_TERMS,
  presetFilters,
  PRESET_NAMES,
  readEliminationMembers,
  readEliminationTeams,
  readProfileCheck,
  rowFromFfs,
  rowFromPick,
  savedTargetsOf,
  scoreOfStats,
  shapeList,
  TARGET_CHECKS_PER_MINUTE,
  TARGET_LEAVING_MAX_MINUTES,
  TARGET_MAX_COUNT,
  TARGET_MAX_IDS,
  TARGET_OKAY_MAX_HOURS,
  TARGET_PRESETS,
  withMember,
  withSavedTargets,
  type EliminationMember,
  type EliminationTeam,
  type EventOptions,
  type FinderRow,
  type Observation,
  type SavedTarget,
  type TargetFilters,
  type TargetPickAnswer,
  type TargetPreset,
} from '@omni-torn/shared';
import { extensionFfsTargets } from '../bridge';
import { readBattleStats } from '../read';
import { server, session, torn, TornError, tornCallsLeft } from '../session';
import { ago, box, duration, num, profileUrl, short, table } from '../ui';

/**
 * Target finder (website roadmap S03 — FF Scouter's target finder, on Omni-Torn's own data):
 *  - Levelling, Respect / chain and Custom search Omni-Torn's player database (POST /api/targets/pick — it rotates, so
 *    every search brings players not handed out lately). Only a rough battle-stat band goes there (packages/shared
 *    target-finder.ts `finderQuery`); your exact fair fight is worked out here from your battle stats (Limited key).
 *  - Too few? FF Scouter's list fills the rest — only through the extension's own FF Scouter connection, and only on
 *    this page (never sent to Omni-Torn's server).
 *  - Saved: your saved targets, the same list as the extension's (lists sync, /api/me/lists), with what the database
 *    knows of them (POST /api/targets/known).
 *  - Live checks: Torn's profile of a player with your own key — one at a time, only when you press Check or Check all,
 *    at most TARGET_CHECKS_PER_MINUTE a minute. What a check saw goes to the player database (terms 6/7: "target checks
 *    you ask for"), no extra call.
 *  - Event presets (website roadmap S16): Not in hospital (seen out lately, plus those leaving hospital within a window —
 *    the database's statuses) and Elimination (a team's members, read with your own key on your click — Torn's teams and
 *    each team's member list, pages of 100 within the site's share of calls — then the database's facts on them; nothing
 *    about the teams is sent anywhere but as a list of ids in the search). FF Scouter's list doesn't fill these in.
 */

const UI_KEY = 'omni-torn.targets';
const PRESETS: TargetPreset[] = ['levelling', 'respect', 'custom', 'outnow', 'elimination', 'saved'];

interface Ui {
  preset: TargetPreset;
  custom: TargetFilters;
  event: EventOptions;
  /** The Elimination team last picked. */
  team: number | null;
  hideAway: boolean;
}

function loadUi(): Ui {
  try {
    const raw = JSON.parse(localStorage.getItem(UI_KEY) ?? 'null') as Partial<Ui> | null;
    const team = typeof raw?.team === 'number' && Number.isInteger(raw.team) && raw.team > 0 ? raw.team : null;
    return { preset: PRESETS.includes(raw?.preset as TargetPreset) ? (raw!.preset as TargetPreset) : 'levelling', custom: cleanFilters(raw?.custom), event: cleanEventOptions(raw?.event), team, hideAway: raw?.hideAway === true };
  } catch {
    return { preset: 'levelling', custom: { ...DEFAULT_CUSTOM }, event: { ...DEFAULT_EVENT }, team: null, hideAway: false };
  }
}

function saveUi(ui: Ui): void {
  try {
    localStorage.setItem(UI_KEY, JSON.stringify(ui));
  } catch {
    /* this visit only */
  }
}

/** Live checks in the last minute (kept across pages: the limit is per minute, not per visit). */
const checkTimes: number[] = [];

function checksLeft(now = Date.now()): number {
  while (checkTimes.length && now - checkTimes[0]! >= 60_000) checkTimes.shift();
  return Math.max(0, Math.min(TARGET_CHECKS_PER_MINUTE - checkTimes.length, tornCallsLeft(now)));
}

export async function renderTargets(view: HTMLElement): Promise<void> {
  const ui = loadUi();
  const me = session.me();
  const signIn = session.get();
  let rows: FinderRow[] = [];
  let listOf: TargetPreset | null = null;
  let yourScore: number | null | undefined;
  let saved: SavedTarget[] | null = null;
  let savedProblem = '';
  let checking = false;
  let stopAsked = false;
  let pendingObs: Observation[] = [];

  const status = h('p', { class: 'form-status', role: 'status', 'data-status': 'targets' });
  const notes = h('div', { class: 'target-notes' });
  const tableHolder = h('div', {});
  const checkAll = h('button', { type: 'button', class: 'btn-small', 'data-action': 'check-all', disabled: true }, 'Check all');
  const hideAway = h('input', { type: 'checkbox', id: 'targets-hide-away', ...(ui.hideAway ? { checked: true } : {}) }) as HTMLInputElement;
  const searchBody = h('div', {});
  const say = (text: string, kind: '' | 'ok' | 'bad' = '') => {
    status.textContent = text;
    status.className = `form-status${kind ? ` ${kind}` : ''}`;
  };

  // ---------------------------------------------------------------- your fair fight

  /** Your battle score (only here: the server gets a rough band) — null without a key that reads battle stats. */
  async function score(): Promise<number | null> {
    if (yourScore !== undefined) return yourScore;
    if (!keyMeets(signIn?.access ?? null, { level: 3, user: ['battlestats'] })) return (yourScore = null);
    try {
      yourScore = scoreOfStats(readBattleStats(await torn('/user/', { selections: 'battlestats' }, 10 * 60_000)));
    } catch {
      yourScore = null;
    }
    return yourScore;
  }

  function scoreNote(): HTMLElement | null {
    if (yourScore) return null;
    return h(
      'p',
      { class: 'notice', 'data-note': 'no-ff' },
      'Fair fight needs your battle stats, which only a Limited Access key reads — so this list isn’t sorted or filtered by fair fight. ',
      h('a', { href: '/account#key', 'data-link': true }, 'Use a Limited key'),
      ' (only a rough band of your stats ever leaves this browser).',
    );
  }

  // ---------------------------------------------------------------- saved targets (lists sync)

  async function loadSaved(): Promise<SavedTarget[]> {
    try {
      const doc = await server<{ lists: Record<string, unknown> | null }>('GET', '/api/me/lists');
      saved = savedTargetsOf(doc.lists);
      savedProblem = '';
    } catch (err) {
      saved = [];
      savedProblem = (err as Error).message;
    }
    return saved;
  }

  /** Change your saved targets: read the server's copy fresh, change it, send it (once more if another device wrote between). */
  async function writeSaved(change: (cur: SavedTarget[]) => SavedTarget[]): Promise<void> {
    for (let i = 0; i < 2; i++) {
      const doc = await server<{ lists: Record<string, unknown> | null; updatedAt: number | null }>('GET', '/api/me/lists');
      const next = change(savedTargetsOf(doc.lists));
      const r = await server<{ applied: boolean; lists: Record<string, unknown> }>('PUT', '/api/me/lists', { lists: withSavedTargets(doc.lists, next), updatedAt: Date.now(), device: 'Website', base: doc.updatedAt ?? null });
      if (r.applied) {
        saved = savedTargetsOf(r.lists);
        return;
      }
    }
    throw new Error('Another device changed your lists just now — try again.');
  }

  const isSaved = (id: number) => !!saved?.some((s) => s.id === id);

  async function toggleSave(r: FinderRow): Promise<void> {
    const on = !isSaved(r.id);
    try {
      await writeSaved((cur) => (on ? [{ id: r.id, name: r.name, at: Date.now() }, ...cur.filter((s) => s.id !== r.id)] : cur.filter((s) => s.id !== r.id)));
      say(on ? `Saved ${r.name ?? `[${r.id}]`}.` : `${r.name ?? `[${r.id}]`} is no longer saved.`, 'ok');
      if (!on && listOf === 'saved') rows = rows.filter((x) => x.id !== r.id);
    } catch (err) {
      say((err as Error).message, 'bad');
    }
    draw();
  }

  /** `fresh`: the list was just written here (the server's answer is in hand — no need to ask again). */
  async function showSaved(fresh = false): Promise<void> {
    listOf = 'saved';
    rows = [];
    say('Reading your saved targets…');
    draw();
    const list = fresh && saved ? saved : await loadSaved();
    if (savedProblem) return say(`Your saved targets couldn’t be read: ${savedProblem}`, 'bad');
    const you = await score();
    const first = list.slice(0, TARGET_MAX_COUNT);
    let known = new Map<number, FinderRow>();
    if (first.length) {
      try {
        const r = await server<TargetPickAnswer>('POST', '/api/targets/known', { ids: first.map((s) => s.id) });
        known = new Map(r.targets.map((t) => rowFromPick(t, you, 'saved')).filter((x): x is FinderRow => !!x).map((x) => [x.id, x]));
      } catch {
        /* names and ids only; checks fill in the rest */
      }
    }
    if (listOf !== 'saved') return;
    rows = first.map((s) => known.get(s.id) ?? emptyRow(s));
    notes.replaceChildren(...[scoreNote()].filter((x): x is HTMLElement => !!x));
    say(list.length ? `${list.length} saved${list.length > first.length ? ` — the newest ${first.length} shown` : ''}.` : 'No saved targets yet — add some above, or press ☆ on any list.', list.length ? 'ok' : '');
    draw();
  }

  const emptyRow = (s: SavedTarget): FinderRow => ({ id: s.id, name: s.name, level: null, factionId: null, estimate: null, estimateFrom: null, ff: null, rawFf: null, lastAction: null, status: null, statusUntil: null, description: null, life: null, seenAt: null, checked: false, source: 'saved' });

  // ---------------------------------------------------------------- searching

  async function find(preset: Exclude<TargetPreset, 'saved'>): Promise<void> {
    const f = presetFilters(preset, ui.custom);
    const isEvent = preset === 'outnow' || preset === 'elimination';
    listOf = preset;
    say('Looking…');
    const you = await score();
    // Elimination: the team's members first (your key; pasted ids instead when given).
    let members = new Map<number, EliminationMember>();
    let ids: number[] | undefined;
    if (preset === 'elimination') {
      const pasted = parseTargetIds(pastedIds, TARGET_MAX_IDS);
      if (pasted.length) ids = pasted;
      else if (ui.team) {
        const got = await teamMembers(ui.team);
        if (listOf !== preset) return;
        if (typeof got === 'string') return say(got, 'bad');
        members = new Map(got.map((m) => [m.id, m]));
        ids = got.map((m) => m.id);
      }
      if (!ids?.length) return say('Pick a team (Read the teams first), or paste player ids from a team’s page.', 'bad');
    }
    let omni: FinderRow[] = [];
    let omniProblem = '';
    try {
      const r = await server<TargetPickAnswer>('POST', '/api/targets/pick', finderQuery(f, you, [], isEvent ? { options: ui.event, ...(ids ? { ids } : {}) } : undefined));
      const at = Date.now();
      omni = r.targets.map((t) => rowFromPick(t, you)).filter((x): x is FinderRow => !!x).map((x) => withMember(x, members.get(x.id), at));
    } catch (err) {
      omniProblem = (err as Error).message;
    }
    if (listOf !== preset) return;
    const sort = isEvent ? 'exit' : preset === 'custom' ? 'ff' : TARGET_PRESETS[preset].sort;
    const opts = { myFactionId: me?.factionId ?? null, me: signIn?.playerId ?? null, now: Date.now() };
    let shaped = shapeList(omni, f, sort, opts);
    let ffsNote: HTMLElement | null = null;
    // Too few known ones: FF Scouter's list, through the extension's own connection (stays on this page). Not for the
    // event presets: it doesn't know who is out of hospital.
    if (!isEvent && shaped.rows.length - shaped.unknown < f.count && signIn) {
      const got = await extensionFfsTargets(ffsFiltersFor(f), signIn.playerId);
      if (listOf !== preset) return;
      if (got && 'targets' in got) {
        const ffs = got.targets.map((t) => rowFromFfs(t, you)).filter((x): x is FinderRow => !!x);
        shaped = shapeList([...omni, ...ffs], f, sort, opts);
        const n = shaped.rows.filter((r) => r.source === 'ffscouter').length;
        if (n) ffsNote = h('p', { class: 'muted small', 'data-note': 'ffscouter' }, `${n} of these come from FF Scouter, through your extension’s FF Scouter connection (they stay on this page).`);
      } else {
        ffsNote = h('p', { class: 'muted small', 'data-note': 'ffscouter-off' }, got ? `FF Scouter’s list wasn’t added: ${got.problem}` : 'With the Omni-Torn extension in this browser and FF Scouter connected in it, FF Scouter’s list fills in when Omni-Torn’s own has too few.');
      }
    }
    rows = shaped.rows;
    if (preset === 'elimination' && members.size) ffsNote = h('p', { class: 'muted small', 'data-note': 'elimination' }, `${num(members.size)} on the team (read with your key); listed: those Omni-Torn’s player database knows who are out of hospital${ui.event.leavingMinutes ? ` or leaving within ${ui.event.leavingMinutes} minutes` : ''}, with what the team list says now.`);
    const left = shaped.left.band + shaped.left.level + shaped.left.active + shaped.left.faction;
    if (omniProblem && !rows.length) say(`Omni-Torn’s player database didn’t answer: ${omniProblem}`, 'bad');
    else say(rows.length ? `${rows.length} found${shaped.unknown ? ` (${shaped.unknown} with stats not known yet, at the end)` : ''}${left ? ` · ${left} left out by your filters or your faction` : ''}. Find again for others.` : 'None found with these filters right now — widen them, or try again later.', rows.length ? 'ok' : '');
    notes.replaceChildren(...[scoreNote(), ffsNote].filter((x): x is HTMLElement => !!x));
    draw();
  }

  // ---------------------------------------------------------------- Elimination (your key, your click)

  let teams: EliminationTeam[] | null = null;
  let myTeam: string | null = null;
  let pastedIds = '';

  /** Torn's teams (one call) and your own team's name from your profile (one more, kept 10 minutes). */
  async function readTeams(): Promise<string | null> {
    try {
      teams = readEliminationTeams(await torn('/v2/torn/elimination', {}, 5 * 60_000));
    } catch (err) {
      teams = [];
      return `Torn didn’t give the Elimination teams (${(err as Error).message}) — is Elimination on? You can paste player ids from a team’s page instead.`;
    }
    if (!teams.length) return 'No Elimination teams right now — Elimination isn’t on. You can paste player ids instead.';
    myTeam = ownEliminationTeam(await torn('/user/', { selections: 'profile' }, 10 * 60_000).catch(() => null));
    if (!ui.team || !teams.some((t) => t.id === ui.team)) ui.team = teams.find((t) => t.name !== myTeam)?.id ?? teams[0]!.id;
    saveUi(ui);
    return null;
  }

  /** A team's members: pages of 100 with your key, within the site's share this minute (kept 5 minutes). */
  async function teamMembers(team: number): Promise<EliminationMember[] | string> {
    const out: EliminationMember[] = [];
    for (let offset = 0; offset < TARGET_MAX_IDS; offset += 100) {
      if (tornCallsLeft() <= 3) {
        if (out.length) break;
        return 'This minute’s share of your Torn calls is used — try again in a minute.';
      }
      try {
        const page = readEliminationMembers(await torn(`/v2/torn/${team}/eliminationteam`, { limit: '100', offset: String(offset) }, 5 * 60_000));
        out.push(...page.members);
        if (!page.next || !page.members.length) break;
      } catch (err) {
        if (out.length) break;
        return `Torn didn’t give that team’s members: ${(err as Error).message}`;
      }
    }
    return out;
  }

  // ---------------------------------------------------------------- live checks (your key, your click)

  /** One check: 'stop' when the minute's checks are used up (nothing asked). */
  async function checkOne(id: number): Promise<'ok' | 'stop' | 'fail'> {
    if (checksLeft() <= 0) return 'stop';
    checkTimes.push(Date.now());
    try {
      const body = await torn(`/user/${id}`, { selections: 'profile' }, 10_000);
      const c = readProfileCheck(body, Date.now());
      if (!c) return 'fail';
      rows = rows.map((r) => (r.id === id ? applyCheck(r, c) : r));
      pendingObs.push(...checkToObservations(id, c));
      return 'ok';
    } catch (err) {
      if (err instanceof TornError && (err.code === 6 || err.code === 7)) {
        rows = rows.map((r) => (r.id === id ? { ...r, status: 'Not found', checked: true, seenAt: Date.now() } : r));
        return 'fail';
      }
      say((err as Error).message, 'bad');
      return err instanceof TornError && (err.code === 5 || err.code === 0) ? 'stop' : 'fail';
    }
  }

  /** What the checks saw → Omni-Torn's player database (only once you agreed to the terms that name it). */
  function sendObservations(): void {
    const list = pendingObs;
    pendingObs = [];
    if (!list.length || (session.me()?.prefs.terms ?? 0) < PLAYER_DB_TERMS) return;
    void server('POST', '/api/players/db/observe', { observations: list }).catch(() => {});
  }

  async function checkRow(id: number): Promise<void> {
    if (checking) return;
    checking = true;
    draw();
    const r = await checkOne(id);
    if (r === 'stop') say(`That’s ${TARGET_CHECKS_PER_MINUTE} checks this minute — Torn’s limit for this site. Try again in a minute.`, 'bad');
    else if (r === 'ok') say('Checked.', 'ok');
    checking = false;
    sendObservations();
    draw();
  }

  async function checkEveryone(): Promise<void> {
    if (checking) {
      stopAsked = true;
      return;
    }
    const todo = rows.filter((r) => !r.checked || !r.seenAt || Date.now() - r.seenAt > 60_000).map((r) => r.id);
    if (!todo.length) return say('Everyone was checked in the last minute.', 'ok');
    checking = true;
    stopAsked = false;
    let done = 0;
    let stopped = false;
    for (const id of todo) {
      if (stopAsked || !rows.some((r) => r.id === id)) continue;
      say(`Checking ${done + 1} of ${todo.length}…`);
      draw();
      const r = await checkOne(id);
      if (r === 'stop') {
        stopped = true;
        break;
      }
      done++;
    }
    checking = false;
    sendObservations();
    const rest = todo.length - done;
    if (stopped) say(`Checked ${done}. ${rest} left — Torn’s limit for this site is ${TARGET_CHECKS_PER_MINUTE} a minute: press Check all again in a minute.`, 'bad');
    else say(stopAsked ? `Stopped after ${done}.` : `Checked ${done}.`, 'ok');
    stopAsked = false;
    draw();
  }

  // ---------------------------------------------------------------- drawing

  function statusCell(r: FinderRow): HTMLElement {
    const now = Date.now();
    // The database keeps Torn's states in lower case ("okay"): shown as Torn writes them.
    const st = r.status ? r.status.charAt(0).toUpperCase() + r.status.slice(1) : '';
    const can = hittable(r, now);
    const left = r.statusUntil && r.statusUntil > now ? duration((r.statusUntil - now) / 1000) : '';
    const text = !st ? '—' : /^(abroad|traveling|travelling)$/i.test(st) && r.description ? r.description : left ? `${st} · ${left}` : st;
    const life = r.checked && r.life && r.life.maximum > 0 && r.life.current < r.life.maximum ? h('span', { class: 'muted small' }, ` life ${num(r.life.current)}/${num(r.life.maximum)}`) : null;
    const seen = st && !r.checked && r.seenAt ? h('span', { class: 'muted small', title: r.fromTeam ? 'From the team list (Torn’s answer to your key)' : 'Seen by an Omni-Torn user' }, ` (${ago(r.seenAt)})`) : null;
    const btn = h('button', { type: 'button', class: 'btn-small btn-check', 'data-action': 'check', disabled: checking, title: 'Ask Torn now (one call with your key)' }, r.checked ? 'Again' : 'Check');
    btn.addEventListener('click', () => void checkRow(r.id));
    return h('span', { class: 'target-status' }, h('span', { class: can === true ? 't-green' : can === false ? 't-red' : '' }, text), life, seen, ' ', btn);
  }

  function draw(): void {
    const now = Date.now();
    const shown = hideAway.checked ? rows.filter((r) => hittable(r, now) !== false) : rows;
    checkAll.disabled = !rows.length;
    checkAll.textContent = checking ? 'Stop' : 'Check all';
    const empty = listOf === null ? 'Pick a list above and press Find targets.' : listOf === 'saved' ? 'No saved targets yet.' : rows.length ? 'Everyone here is out of reach right now.' : 'Nothing found yet.';
    tableHolder.replaceChildren(
      table(
        shown,
        [
          {
            label: 'Player',
            get: (r) => h('span', {}, h('a', { href: profileUrl(r.id), target: '_blank', rel: 'noopener noreferrer', class: 'target-name', 'data-id': String(r.id) }, r.name ?? `Player ${r.id}`), h('span', { class: 'muted small' }, ` [${r.id}]`), r.source === 'ffscouter' ? h('span', { class: 'tag tag-ffs', title: 'From FF Scouter’s list (through your extension)' }, 'FF Scouter') : null),
            sort: (r) => (r.name ?? '').toLowerCase(),
          },
          { label: 'Attack', get: (r) => h('a', { href: `https://www.torn.com/loader.php?sid=attack&user2ID=${r.id}`, target: '_blank', rel: 'noopener noreferrer', class: 'target-attack' }, 'Attack ↗') },
          { label: 'Level', get: (r) => (r.level === null ? '—' : String(r.level)), sort: (r) => r.level ?? 0, cls: 'r' },
          {
            label: 'FF',
            get: (r) => (r.ff !== null ? h('span', { title: r.rawFf !== null && r.rawFf > 3 ? 'Capped at 3 — stronger than that' : 'Fair fight you’d get' }, r.ff.toFixed(2)) : h('span', { class: 'muted', title: r.estimate === null ? 'Stats not known yet' : 'Needs your battle stats' }, '?')),
            sort: (r) => r.ff ?? 0,
            cls: 'r',
          },
          { label: 'Estimate', get: (r) => (r.estimate === null ? h('span', { class: 'muted' }, 'not known') : h('span', {}, short(r.estimate), r.estimateFrom ? h('span', { class: 'muted small' }, ` ${r.estimateFrom}`) : null)), sort: (r) => r.estimate ?? 0, cls: 'r' },
          { label: 'Last action', get: (r) => (r.lastAction ? ago(r.lastAction) : '—'), sort: (r) => -(r.lastAction ?? now) },
          { label: 'Status', get: statusCell, sort: (r) => (hittable(r, now) === true ? 2 : hittable(r, now) === null ? 1 : 0) },
          {
            label: '',
            get: (r) => {
              const star = h('button', { type: 'button', class: 'btn-icon', 'data-action': 'save', 'aria-pressed': isSaved(r.id) ? 'true' : 'false', title: isSaved(r.id) ? 'Saved — press to forget' : 'Save (also in the extension’s saved targets)' }, isSaved(r.id) ? '★' : '☆');
              star.addEventListener('click', () => void toggleSave(r));
              const del = h('button', { type: 'button', class: 'btn-icon', 'data-action': 'remove', title: listOf === 'saved' ? 'Remove from your saved targets' : 'Remove from this list' }, '✕');
              del.addEventListener('click', () => {
                if (listOf === 'saved') return void toggleSave(r);
                rows = rows.filter((x) => x.id !== r.id);
                draw();
              });
              return h('span', { class: 'target-tools' }, star, del);
            },
          },
        ],
        { empty, remember: 'targets' },
      ),
    );
  }

  // ---------------------------------------------------------------- the search box

  const numField = (label: string, key: keyof TargetFilters, min: number, max: number, step: number) => {
    const input = h('input', { type: 'number', min: String(min), max: String(max), step: String(step), value: String(ui.custom[key]), 'data-field': key, inputmode: step < 1 ? 'decimal' : 'numeric' }) as HTMLInputElement;
    input.addEventListener('change', () => {
      ui.custom = cleanFilters({ ...ui.custom, [key]: Number(input.value) }, ui.custom);
      input.value = String(ui.custom[key]);
      saveUi(ui);
    });
    return h('label', { class: 'target-field' }, h('span', {}, label), input);
  };
  const eventField = (label: string, key: keyof EventOptions, min: number, max: number) => {
    const input = h('input', { type: 'number', min: String(min), max: String(max), step: '1', value: String(ui.event[key]), 'data-field': key, inputmode: 'numeric' }) as HTMLInputElement;
    input.addEventListener('change', () => {
      ui.event = cleanEventOptions({ ...ui.event, [key]: Number(input.value) }, ui.event);
      input.value = String(ui.event[key]);
      saveUi(ui);
    });
    return h('label', { class: 'target-field' }, h('span', {}, label), input);
  };
  const boxField = (label: string, key: 'inactive' | 'factionless') => {
    const input = h('input', { type: 'checkbox', 'data-field': key, ...(ui.custom[key] ? { checked: true } : {}) }) as HTMLInputElement;
    input.addEventListener('change', () => {
      ui.custom = { ...ui.custom, [key]: input.checked };
      saveUi(ui);
    });
    return h('label', { class: 'target-check' }, input, ` ${label}`);
  };

  function drawSearch(): void {
    const p = ui.preset;
    const go = h('button', { type: 'submit', class: 'btn-primary', 'data-action': 'find' }, 'Find targets');
    if (p === 'saved') {
      const input = h('input', { type: 'text', id: 'targets-add', placeholder: 'Player ids or profile links', autocomplete: 'off' }) as HTMLInputElement;
      const form = h('form', { class: 'target-form' }, h('p', { class: 'muted' }, 'Your saved targets — the same list as the extension’s (with “same settings, lists and notes on all my devices” on).'), h('div', { class: 'key-row' }, input, h('button', { type: 'submit', class: 'btn-primary', 'data-action': 'add' }, 'Add')));
      form.addEventListener('submit', (e) => {
        e.preventDefault();
        const ids = parseTargetIds(input.value);
        if (!ids.length) return say('Type player ids (or paste profile links).', 'bad');
        void writeSaved((cur) => [...ids.filter((id) => !cur.some((s) => s.id === id)).map((id) => ({ id, name: null, at: Date.now() })), ...cur])
          .then(() => {
            input.value = '';
            return showSaved(true);
          })
          .catch((err: Error) => say(err.message, 'bad'));
      });
      searchBody.replaceChildren(form);
      return;
    }
    const isEvent = p === 'outnow' || p === 'elimination';
    const form = h(
      'form',
      { class: 'target-form' },
      ...(isEvent ? [h('p', { class: 'muted', 'data-blurb': p }, EVENT_BLURBS[p])] : []),
      p === 'elimination' ? teamPicker() : null,
      p === 'custom' || isEvent
        ? h(
            'div',
            { class: 'target-fields' },
            numField('Level from', 'levelMin', 1, 100, 1),
            numField('to', 'levelMax', 1, 100, 1),
            numField('Fair fight from', 'ffMin', 1, 3, 0.05),
            numField('to', 'ffMax', 1, 3, 0.05),
            ...(p === 'custom' ? [boxField(`Inactive only (${INACTIVE_DAYS}+ days)`, 'inactive')] : []),
            ...(p === 'elimination' ? [] : [boxField('No faction only', 'factionless')]),
          )
        : h('p', { class: 'muted' }, TARGET_PRESETS[p].blurb),
      isEvent
        ? h('div', { class: 'target-fields' }, eventField('Seen out of hospital in the last (hours)', 'okayHours', 1, TARGET_OKAY_MAX_HOURS), eventField('Also leaving hospital within (minutes, 0 = no)', 'leavingMinutes', 0, TARGET_LEAVING_MAX_MINUTES))
        : null,
      h('div', { class: 'toolbar' }, numField('How many', 'count', 1, TARGET_MAX_COUNT, 1), go),
    );
    form.addEventListener('submit', (e) => {
      e.preventDefault();
      // A number typed but not yet "changed" (Enter in the box): take it now.
      for (const input of form.querySelectorAll<HTMLInputElement>('input[type=number]')) input.dispatchEvent(new Event('change'));
      go.disabled = true;
      void find(p).finally(() => (go.disabled = false));
    });
    searchBody.replaceChildren(form);
  }

  /** Elimination: the team to hit (Torn's teams, read on your click) or player ids pasted from a team's page. */
  function teamPicker(): HTMLElement {
    const holder = h('div', { class: 'target-team', 'data-team-picker': '' });
    const paste = h('input', { type: 'text', id: 'targets-team-ids', placeholder: 'Or paste player ids / profile links from a team’s page', autocomplete: 'off', value: pastedIds }) as HTMLInputElement;
    paste.addEventListener('input', () => (pastedIds = paste.value));
    const drawPicker = () => {
      if (!teams) {
        const read = h('button', { type: 'button', class: 'btn-small', 'data-action': 'read-teams', title: 'Asks Torn once with your key' }, 'Read the teams');
        read.addEventListener('click', () => {
          read.disabled = true;
          say('Reading the Elimination teams…');
          void readTeams().then((problem) => {
            if (problem) say(problem, 'bad');
            else say(`${teams!.length} teams.`, 'ok');
            drawPicker();
          });
        });
        holder.replaceChildren(h('div', { class: 'toolbar' }, read), paste);
        return;
      }
      if (!teams.length) return void holder.replaceChildren(paste);
      const select = h(
        'select',
        { id: 'targets-team', 'data-field': 'team' },
        ...teams.map((t) => h('option', { value: String(t.id), ...(t.id === ui.team ? { selected: true } : {}) }, `${t.position ? `#${t.position} ` : ''}${t.name}${t.score !== null ? ` · ${num(t.score)} points` : ''}${t.lives !== null ? ` · ${num(t.lives)} lives` : ''}${t.eliminated ? ' · out' : ''}${t.name === myTeam ? ' — your team' : ''}`)),
      ) as HTMLSelectElement;
      select.addEventListener('change', () => {
        ui.team = Number(select.value) || null;
        saveUi(ui);
      });
      holder.replaceChildren(h('label', { class: 'target-field' }, h('span', {}, 'Team'), select), paste);
    };
    drawPicker();
    return holder;
  }

  const presetTabs = h('div', { class: 'seg', role: 'tablist', 'data-tabs': 'presets' });
  for (const p of PRESETS) {
    const b = h('button', { type: 'button', role: 'tab', 'data-preset': p, class: p === ui.preset ? 'active' : '', 'aria-selected': p === ui.preset ? 'true' : 'false' }, PRESET_NAMES[p]);
    b.addEventListener('click', () => {
      for (const x of presetTabs.children) {
        x.classList.toggle('active', x === b);
        x.setAttribute('aria-selected', x === b ? 'true' : 'false');
      }
      ui.preset = p;
      saveUi(ui);
      drawSearch();
      if (p === 'saved') void showSaved();
    });
    presetTabs.append(b);
  }

  checkAll.addEventListener('click', () => void checkEveryone());
  hideAway.addEventListener('change', () => {
    ui.hideAway = hideAway.checked;
    saveUi(ui);
    draw();
  });

  view.replaceChildren(
    h('div', { class: 'page-head' }, h('h1', {}, 'Target finder')),
    h('p', { class: 'muted' }, 'Players to attack, from Omni-Torn’s own player database: every search brings players not handed out lately. Your fair fight is worked out in this browser; only a rough band of your stats is sent.'),
    box('Find targets', [presetTabs, searchBody], { id: 'target-search' }),
    box(
      'Targets',
      [
        h('div', { class: 'toolbar' }, checkAll, h('label', { class: 'target-check', for: 'targets-hide-away' }, hideAway, ' Hide those you can’t hit now'), h('span', { class: 'muted small' }, `Checks ask Torn with your key — one at a time, at most ${TARGET_CHECKS_PER_MINUTE} a minute.`)),
        status,
        notes,
        tableHolder,
      ],
      { id: 'targets' },
    ),
  );
  drawSearch();
  draw();
  // Your saved ones (for the ☆ on every list); the Saved tab draws them.
  if (ui.preset === 'saved') void showSaved();
  else void loadSaved().then(() => draw());
}
